import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { api } from '../../../utils/api';
import Pagination, { getPageSize } from '../../Pagination';
import DlBtn from '../../DlBtn';
import type { AwConvertAnswer, AwConvertVerdict, AwDoc, AwDocActions, AwDocKind, AwDocOffer, AwDocPage, AwDocVoided, AwOfferStatus } from '../../../types';
import type { AwCtx } from '../ctx';
import { Dialog } from '../dialog';
import NewDocBtn from '../NewDocBtn';
import { DocEntriesBtn, DocEntriesDialog, type DocCtx } from '../docEntries';
import RowMenu, { type RowMenuEntry } from '../RowMenu';
import { FigureLine, GREYED, NoCell, SmallLine, STRUCK, StatusCell, salesStatus } from '../listCells';
import { OfferDot } from '../relatedDocs';
import { stillStands } from '../sourceDoc';   // the same deliberate cycle sourceDoc.tsx's header describes: used inside a body
import { addCompanyRegions } from '../../../utils/region';
import { CreateDocModal, CreateNoteModal } from '../../MerchantDoc/CreateDocModal';
import { buildConvertSeed, buildCopySeed, type ConvertSeed, type ConvertSeedType } from '../../MerchantDoc/convertSeed';
import { CreateReceiptModal, EditReceiptModal } from '../../MerchantDoc/ReceiptModals';
import { InBooksBanner } from '../../MerchantDoc/InBooksBanner';
import { SendDocModal, SendHistory, type SendDocApi } from '../../MerchantDoc/SendDocModal';
import { ReasonDialog } from './Purchases';
import { ReverseEntryBtn } from '../reverseEntryBtn';   // batch 11 R3: "Reverse JV-…" opens the document's entries panel
import { RecordEvidence } from '../attachments';   // owner 2026-11-02: the document's supporting files and linked records
import { SortChip, SortTh, sortArgs, useListSort, type ListId } from '../listSort';   // batch 12 tail T3 (FS-11): headers that sort, and stay
import { WORKSPACE_LAYER } from '../../MerchantDoc/shell';
import { dayFirstDeep, docRefusalText, docTypeWord, refusalText, type MerchantReceiptApi } from '../../MerchantDoc/shared';
import { timeHM, todayISO } from '../../../utils/region';
import { docPlainOf, merchantToday, thaiModeOf } from '../../ClientPortal/plainMode';
import { amt, btn, card, dmy, errBox, inp, note, num, openBlob, table, td, th } from '../ui';

// ── Sales: the client company's own documents (owner 2026-10-13; create/edit owner 2026-10-15) ───
// Every row comes from /api/acct-workspace/docs/:kind, which the server scopes by the merchant row
// linked to this client (company_id = merchant.id — never a JOIN on invoices.client_id, which points
// at the merchant's own customer list). Voided documents are hidden server-side, unless "Show voided"
// asks for them (owner 2026-11-02, DocsPane below). Creating and editing run the PORTAL'S OWN handlers behind the workspace gates (routes/acctWorkspace.ts
// asMerchant), through the shared dialogs in src/components/MerchantDoc — numbering stays the
// merchant's own series, and the staff member's name goes on the audit columns.

const DT: Record<string, string> = {
  invoice: 'aw_dt_invoice', proforma: 'aw_dt_proforma', tax_invoice: 'aw_dt_tax_invoice', tax_invoice_receipt: 'aw_dt_tax_invoice_receipt',
  credit_note: 'aw_dt_credit_note', debit_note: 'aw_dt_debit_note',
};
const r2 = (n: number) => Math.round(n * 100) / 100;
/** The portal's own per-row figure (ClientPortal `owing`): what is still owed on a document — THE
 *  outstanding as the server measures it (receipts round 4, item 2: services/merchantOutstanding.ts,
 *  served on every invoices-table row as `outstanding` — with the buyer's WHT, less what credit notes took
 *  off). Never re-derived here. */
const owing = (d: AwDoc) => r2(Number(d.outstanding) || 0);

/**
 * COA-D2 — which of the families may be asked "what did this post" (owner 2026-10-19).
 *
 * ⛔ The question goes to ONE resolver (server/src/services/journalLinks.ts), and it answers for the
 * invoices table's own doc_types — invoice, proforma, tax_invoice, tax_invoice_receipt, credit_note,
 * debit_note — and for receipts. A QUOTATION is the one family it refuses: the route answers
 * `postsEntries: false` with "A quotation is an offer — it is not an accounting event", so offering
 * the button (or, since 2026-11-02, the row menu's item) there would put a control on screen whose
 * only possible answer is "never". It is offered where the resolver answers and nowhere else; the
 * kind sent is the row's STORED doc_type, never a type guessed from the shape of its number.
 */
const postsEntries = (kind: AwDocKind): boolean => kind !== 'quotation';

/** Why the row menu's Edit is greyed, by the verdict's `code` (the server's editBlocked cause). A cause not worded here
 *  says the server's own sentence, as the alert always did. ⛔ Every key written in full, so a grep finds each. */
const EDIT_WITHHELD_KEY: Record<string, string> = {
  notes: 'aw_doc_menu_edit_notes', filed: 'aw_doc_menu_edit_filed', rejected: 'aw_doc_menu_edit_rejected',
};

/**
 * Why the row menu's Void is greyed, or why the window cannot go on, in the reader's language (owner 2026-11-02). The verdict's
 * `refusal` is the body POST …/void answers, with its cause beside it. The refusals the portal already words go through the
 * portal's own two functions; those words say "contact your accountant" for a document in the books, and this reader IS the
 * accountant, so that one is said here. A cause worded nowhere says the server's own sentence, as the portal does.
 */
const endWhy = (t: TFn, raw: any, entries: any[] = raw?.entries || []): string => {
  const p = dayFirstDeep(raw);
  const q = p?.params || {};
  const monthOf = (ym: any) => String(ym || '').replace(/^(\d{4})-(\d{2})$/, '$2/$1');
  const inBooks = (rows: any[], docNos: any[]): string => {
    const shut = rows.some((e: any) => !!e?.closed && !e?.reversalDraft);
    return t(shut ? 'aw_doc_end_in_books_closed' : 'aw_doc_end_in_books', { docNos: docNos.join(', ') });
  };
  switch (String(p?.code || '')) {
    case 'quotation_billed': return t('aw_doc_end_billed', { nos: q.nos });
    case 'quotation_receipts': return t('aw_doc_end_quotation_receipts', { nos: q.nos });
    case 'notes_first': return t('aw_doc_end_notes');
    case 'filed': return t('aw_doc_end_filed', { month: monthOf(q.month) });
    case 'in_books': return inBooks(entries, Array.isArray(q.docNos) ? q.docNos : []);
    case 'bank_matched': return t(Number(q.n) > 1 ? 'aw_doc_end_bank_matched_n' : 'aw_doc_end_bank_matched', { n: q.n, date: q.date, amount: amt(q.amount) });
    case 'receipt_bill_settled':
      return entries.some((e: any) => !!e?.closed && !e?.reversalDraft)
        ? inBooks(entries, Array.isArray(q.docNos) ? q.docNos : [])
        : t('aw_doc_end_bill_settled', { billNo: q.billNo, docNos: (Array.isArray(q.docNos) ? q.docNos : []).join(', ') });
    case 'by_hand': return t('aw_doc_end_by_hand', { entry: q.entry });
    case 'tax_module': return t('aw_doc_end_tax_module');
    case 'void_note_held':
      if (q.inner?.code === 'in_books') {
        const w = docTypeWord(t, q.noteType);
        return t('p_doc_void_note_held', { docNo: q.docNo, noteNo: q.noteNo, noteWord: w.docWord, noteWordLc: w.docWordLc, reason: inBooks(q.inner.entries || [], q.inner.params?.docNos || []) });
      }
      break;
    default:
  }
  const e = { payload: p };
  return refusalText(t, e, amt) || docRefusalText(e, t) || String(p?.error || '');
};

/** Why the row menu's Send Email is greyed, by the verdict's `code` (portalDocs.ts sendEnvelopeOf). ⛔ Every key written in full, so a grep finds each. */
const SEND_WITHHELD_KEY: Record<string, string> = {
  no_reply_to: 'aw_doc_send_no_reply_to', no_sender: 'aw_doc_send_no_sender', no_mailbox: 'aw_doc_send_no_mailbox', rejected: 'aw_doc_send_rejected',
};
const sendWhy = (t: TFn, s: { code: string; reason: string }): string => (SEND_WITHHELD_KEY[s.code] ? t(SEND_WITHHELD_KEY[s.code]) : s.reason);

/** The 50 ทวิ upload's limit: routes/portalDocs.ts `upload` (multer's fileSize). */
const WHT_MAX_BYTES = 10 * 1024 * 1024;

/** "Voided by …": who, when (UK day-first as the list's dates are, the tenant's zone, to the minute) and why, on the row and in its window. */
const voidedLine = (t: TFn, v: AwDocVoided): string => {
  const at = v.at ? new Date(v.at * 1000) : null;
  return t('aw_doc_voided_line', { by: v.by || '—', when: at ? `${dmy(todayISO(at))} ${timeHM(at)}` : '—', reason: v.reason || '—' });
};

/** "Rejected by …": who, when (the tenant's zone, to the minute) and why, on the row and in its window (owner 2026-11-02, services/quotationStage.ts). */
const rejectedLine = (t: TFn, o: AwDocOffer): string => {
  const at = o.at ? new Date(o.at * 1000) : null;
  return t('aw_offer_reject_line', { by: o.by || '—', when: at ? `${dmy(todayISO(at))} ${timeHM(at)}` : '—', reason: o.reason || '—' });
};

// A line's stored classification, read back in the portal's own words. Snake_case fields because
// the detail route returns the row as stored; '' stays "not stated" — never answered for anyone.
type TFn = TFunction<'translation', undefined>;
const vatLabel = (t: TFn, it: any): string => {
  const keys: Record<string, string> = { standard: 'p_vat_opt_standard', standard_incl: 'p_vat_opt_standard_incl', zero: 'p_vat_opt_zero', exempt: 'p_vat_opt_exempt', none: 'p_vat_opt_none' };
  const k = keys[String(it.vat_category || '')];
  return k ? t(k, { rate: Number(it.vat_rate) || 7 }) : '';
};
const chargeLabel = (t: TFn, it: any): string => {
  const role = String(it.charge_role || ''), billed = String(it.supplier_invoice_billed_to || '');
  if (role === 'own_supply') return t('p_cdoc_charge_opt_own');
  if (role === 'disbursement' && billed === 'customer') return t('p_cdoc_charge_opt_cust');
  if (role === 'disbursement' && billed === 'self') return t('p_cdoc_charge_opt_self');
  return '';
};

/**
 * What this window needs to know about the document BEFORE its own fetch lands: the id it reads by,
 * and the number and type it heads itself with while the read is in flight.
 *
 * ⛔ A SUBSET OF AwDoc, not a second shape (batch 6, COA-D10). Opened from the list, every field is
 * the list row's. Opened from a posted voucher's "source document" (CompanyWorkspace/sourceDoc.tsx)
 * there is no list row at all: `id`, `doc_no` and `doc_type` come from the SERVER's own resolver
 * (services/journalLinks.ts), and `status` / `linked_no` are not known until the detail arrives —
 * which is why both fall back to the fetched row below instead of being passed as ''.
 */
export type AwDocRef = Pick<AwDoc, 'id' | 'doc_no' | 'doc_type'> & Partial<Pick<AwDoc, 'status' | 'linked_no'>>;

// ── The row click's detail: the document's own lines, read-only (owner 2026-10-15) ───────────────
// Everything shown is the stored row — figures, classification, remark — with no recomputation:
// the PDF and the books read these same columns, so the three can never tell different stories.
export function DocDetail({ ctx, kind, doc, onClose, onPdf, busyPdf, entries = true, voided = false }: {
  /** ⛔ The client id alone — see DocCtx in docEntries.tsx. Opened from the Sales list this is the
   *  shell's whole ctx; opened from a posted voucher (COA-D10) it is the client the server said the
   *  voucher belongs to, and there is no shell ctx to pass. */
  ctx: DocCtx; kind: AwDocKind; doc: AwDocRef; onClose: () => void; onPdf: () => void; busyPdf: boolean;
  /** COA-D2's button. ⛔ Off when this window was itself reached FROM a voucher (COA-D10): the
   *  entries panel would walk the reader back to the voucher they are standing on, one window
   *  deeper, and "what did this document post" is not a question somebody arriving from the posting
   *  is asking. The window is otherwise the same one the Sales list opens — never a second copy. */
  entries?: boolean;
  /** A voided row, from "Show voided" (owner 2026-11-02): its lines with who, when and why, and nothing to act with. No PDF: the
   *  portal has none of a void either (merchantDocPdf refuses one). No entries panel: it posts and reverses. */
  voided?: boolean;
}) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    // The row may have gone since the list was read (voided in another tab), and a 404 with a JSON body pages the admins: the verdict,
    // which always answers 200, is asked first. A row listed as voided is read as one, and asks GET /doors, which answers for a voided row
    // too (it may have been released since, and that row has no window). A verdict that cannot be read does not stand in the way of a
    // read: only "gone" stops it.
    (voided ? stillStands(ctx.clientId, kind, doc.id) : api.awDocActions(ctx.clientId, kind, doc.id).then((v: AwDocActions) => !!v.found, () => true))
      .then((there: boolean) => {
        if (!alive) return null;
        if (!there) { setErr(t('aw_doc_menu_gone')); return null; }
        return api.awDoc(ctx.clientId, kind, doc.id, { voided }).then((r: any) => { if (alive) setD(r); });
      })
      .catch((e: any) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [ctx.clientId, kind, doc.id, voided]); // eslint-disable-line react-hooks/exhaustive-deps

  const items: any[] = (d?.items || []).filter((it: any) => !it.is_note);
  const notes: any[] = (d?.items || []).filter((it: any) => !!it.is_note);
  const hasCharge = items.some(it => chargeLabel(t, it));
  const row = (label: string, v: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 8, fontSize: 12.5 }}>
      <div style={{ color: 'var(--text-tertiary)', width: 128, flex: 'none' }}>{label}</div>
      <div style={{ minWidth: 0 }}>{v}</div>
    </div>
  );
  const sum = (label: string, v: number | null | undefined, strong = false) => (v == null ? null : (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 14, fontSize: 12.5, fontWeight: strong ? 700 : 400 }}>
      <div style={{ color: strong ? undefined : 'var(--text-tertiary)' }}>{label}</div>
      <div style={{ width: 120, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{amt(v)}</div>
    </div>
  ));
  const isReceipt = kind === 'receipt';
  // A quotation's is the status a person chooses, the list's own expression (services/quotationStage.ts); `status` stays payment progress.
  const docWord = salesStatus(t, String(d?.doc_type || doc.doc_type || kind), voided ? 'void' : String(d?.offer?.status || d?.status || doc.status || ''));
  // Nothing is owed on a proforma or a credit note (the list's rule), so neither says Outstanding.
  const owed = d && !voided && !['proforma', 'credit_note'].includes(String(d.doc_type || doc.doc_type)) ? Number(d.outstanding) || 0 : 0;
  // The entries panel is asked by the document's STORED doc_type (or its family key) — the same kind as the button below.
  const storedKind = doc.doc_type || kind;
  // ── FS-2: a document window, full screen ──────────────────────────────────────────────────────
  // The lines are a table that GROWS: a thirty-line bill was read four rows at a time through a
  // 760px card. ⛔ No `dirty` is passed, and that is a statement about this subtree rather than an
  // omission — nothing in it is editable (no input, no textarea, no select, no onChange; asserted in
  // testDirtyClose.ts §2), so the X, Esc and the backdrop have nothing to ask about. The moment
  // somebody puts a field in here, that assertion fails and this comment stops being true.
  // (Owner 2026-11-02) The supporting files' card has a file picker and a search box, in its own file, and neither is the document's:
  // an upload takes effect as it is picked and is never Saved, so closing this window still has nothing to lose.
  // The title keeps carrying its own number: `doc_no · type` is one phrase here and splitting it
  // into the shell's `docNo` would need a family label this component is not handed.
  return (
    <Dialog title={`${doc.doc_no}${DT[doc.doc_type] ? ` · ${t(DT[doc.doc_type])}` : ''}`} onClose={onClose} fullscreen
      footer={voided ? undefined : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {/* COA-D2, where the question is actually asked: the accountant is looking AT the document.
              The panel is read-only and opens over this window — the shell carries the nesting. */}
          {entries && postsEntries(kind) && <DocEntriesBtn ctx={ctx} kind={doc.doc_type || kind} id={doc.id} docNo={doc.doc_no} style={btn} />}
          <button style={btn} onClick={onPdf} disabled={busyPdf}>
            <i className={busyPdf ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('aw_col_open_pdf')}
          </button>
        </div>
      )}>
      {err && <div style={errBox}>{err}</div>}
      {!d && !err ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div> : d && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {voided && d.voided && (
            <div role="status" data-doc-voided="" style={errBox}>
              <i className="ti ti-ban" aria-hidden /> {voidedLine(t, d.voided)}
            </div>
          )}
          {!voided && d.offer?.status === 'rejected' && (
            <div role="status" data-doc-rejected="" style={errBox}>
              <i className="ti ti-circle-x" aria-hidden /> {rejectedLine(t, d.offer)}
            </div>
          )}
          {/* Batch 11 R1 (COA-D9): the entries this document has in the books, as the server answered them —
              read-only, above the parties. A read-only seat sees it too; nothing here writes. */}
          <InBooksBanner rows={d.inBooks} who="staff" blocked={d.fullEditBlocked || ''}
            action={d.fullEditBlocked || voided ? undefined : (r => <ReverseEntryBtn ctx={ctx} kind={storedKind} id={doc.id} docNo={doc.doc_no} entry={r} style={{ ...btn, padding: '2px 8px', fontSize: 11.5 }} />)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {row(t('aw_col_customer'), <>{d.client_name}{d.client_tax_id ? <span style={{ color: 'var(--text-tertiary)' }}> · {d.client_tax_id}</span> : ''}</>)}
            {!!d.client_address && row(t('aw_dd_address'), d.client_address)}
            {row(t('aw_col_date'), <>{dmy(isReceipt ? (d.payment_date || d.issue_date) : d.issue_date)}{!isReceipt && d.due_date ? ` → ${dmy(d.due_date)}` : ''}</>)}
            {/* The words the list and the portal print (listCells.tsx salesStatus), not the stored value: an issued bill is stored 'draft'. */}
            {row(t('aw_col_status'), <span style={docWord.tone} data-status-value={String((!voided && d.offer?.status) || d.status || doc.status || '')}>{docWord.word}</span>)}
            {/* The paired document's number: the list row's when this window was opened from the
                list, else the detail route's own (added for COA-D10 — the two are the same SQL
                expression, so they cannot disagree). */}
            {!!(doc.linked_no || d.linked_no) && row(t('aw_col_linked'), doc.linked_no || d.linked_no)}
            {/* The quotation it was issued from (owner 2026-11-02): new documents only, as on the sheet's Ref line. */}
            {!!d.quotation_ref_no && row(t('aw_dd_from_quotation'), <span data-doc-from-quotation="">{d.quotation_ref_no}</span>)}
            {/* The statement lines matched to a quotation: a Rejected one takes no new match and keeps these (owner 2026-11-02). */}
            {!!d.bankMatches?.length && row(t('aw_dd_bank_matched'), <span data-doc-bank-matches="">{d.bankMatches.map((l: any) => `${dmy(l.date)} · ${amt(l.amount)}`).join(', ')}</span>)}
            {!!d.remark && row(t('aw_dd_remark'), <span style={{ whiteSpace: 'pre-wrap' }}>{String(d.remark).replace(/<[^>]+>/g, ' ')}</span>)}
          </div>
          {!!items.length && (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={table}>
                <thead><tr>
                  <th style={th}>{t('aw_dd_item')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_dd_qty')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_dd_unit_price')}</th>
                  <th style={th}>{t('aw_dd_vat')}</th>
                  {hasCharge && <th style={th}>{t('p_cdoc_charge_title')}</th>}
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_dd_amount')}</th>
                </tr></thead>
                <tbody>
                  {items.map((it: any) => (
                    <tr key={it.id}>
                      <td style={td}>{it.name}{it.description ? <div style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>{it.description}</div> : null}</td>
                      <td style={num}>{Number(it.quantity) || 0}</td>
                      <td style={num}>{amt(it.unit_price)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{vatLabel(t, it) || '—'}</td>
                      {hasCharge && <td style={{ ...td, whiteSpace: 'nowrap' }}>{chargeLabel(t, it) || t('p_cdoc_charge_unset')}</td>}
                      <td style={num}>{amt(it.pre_vat_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {notes.map((it: any) => <div key={it.id} style={note}>{it.name || it.description}</div>)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {isReceipt ? (<>
              {sum(t('aw_dd_received'), d.amount)}
              {sum(t('aw_dd_wht'), d.wht_amount)}
              {sum(t('aw_col_net'), d.net_to_pay, true)}
            </>) : (<>
              {Number(d.total_discount) > 0 ? sum(t('aw_dd_discount'), d.total_discount) : null}
              {sum(t('aw_dd_vat'), d.vat_amount)}
              {Number(d.wht_amount) > 0 ? sum(t('aw_dd_wht'), d.wht_amount) : null}
              {sum(t('aw_col_total'), d.total_amount, true)}
              {sum(t('aw_dd_net_to_pay'), d.net_to_pay)}
              {Number(d.paid_amount) > 0 ? sum(t('aw_col_paid'), d.paid_amount) : null}
              {owed > 0.005 ? sum(t('aw_col_outstanding'), owed) : null}
              {!voided && Number(d.credited) > 0.005 && <div data-owed-credited="" style={{ textAlign: 'right', fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('p_owed_received_credited', { received: amt(d.paid_amount), credited: amt(d.credited) })}</div>}
            </>)}
          </div>
          {/* Who it was emailed to (owner 2026-11-02): recipient, date and time, the mail server's answer, who sent it. */}
          <SendHistory rows={d.sendHistory} />
          <RecordEvidence clientId={ctx.clientId} kind={String(doc.doc_type || kind)} id={doc.id} />
        </div>
      )}
    </Dialog>
  );
}

// ⛔ Every family but `note`: both notes at once is a door for a voucher's jump (DocDetail above), not a list. It has no
// entry in listSort.tsx, so mounting it here would read a sort that does not exist.
export function DocsPane({ ctx, kind, title }: { ctx: AwCtx; kind: Exclude<AwDocKind, 'note'>; title: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [data, setData] = useState<AwDocPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [sel, setSel] = useState<AwDoc | null>(null);   // the row whose lines are open (owner 2026-10-15)
  // The row whose entries panel is open. Held HERE, not in the row's menu: the menu closes as it opens the panel, and
  // whatever it held would unmount with it (owner 2026-11-02).
  const [entriesOf, setEntriesOf] = useState<(AwDoc & { focusEntryId?: string }) | null>(null);
  // The row whose Void or Delete window is open: held here for the same reason.
  const [ending, setEnding] = useState<{ d: AwDoc; door: 'void' | 'delete' } | null>(null);
  // The row whose Send Email window is open: held here for the same reason.
  const [sending, setSending] = useState<AwDoc | null>(null);
  // The quotation whose "why is it Rejected" window is open: held here for the same reason.
  const [rejecting, setRejecting] = useState<AwDoc | null>(null);
  // "Show voided" (owner 2026-11-02): the portal's own toggle, its words and its meaning: the live rows and the voided ones.
  const [showVoided, setShowVoided] = useState(false);
  // ── Staff create / edit (owner 2026-10-15, Sales phase 2; receipts FS-4, owner 2026-10-20) ────
  // The SHARED dialogs (src/components/MerchantDoc) run here against the workspace's adapter: the
  // server executes the portal's own handlers behind the workspace gates, so what staff issue and
  // what the client issues are one implementation. Receipts too, since batch 2 lifted their two
  // dialogs into MerchantDoc/ReceiptModals.tsx: issuing one is the portal's createReceiptRoute, and
  // correcting one is the portal's LIMITED patch (remark + bank account, a reason on a Thai receipt)
  // asked of the portal's own /editable first. Adjustment notes are never edited, only issued.
  const [createOpen, setCreateOpen] = useState(false);
  // What the New window opens as when a quotation's Status dropdown opened it (owner 2026-11-02): a seeded form, or a receipt on a bill. The same two windows, through the same entry points.
  // A row's Copy opens the same window the same way (openCopy below), its seed saying it is a copy.
  const [convert, setConvert] = useState<{ seed?: ConvertSeed; receiptOn?: { id: string; docNo: string } } | null>(null);
  const [editDoc, setEditDoc] = useState<any>(null);
  const [busyEdit, setBusyEdit] = useState('');
  const [profile, setProfile] = useState<any>(null);
  const [bump, setBump] = useState(0);
  // One hidden file input per receipt row — the row menu's "Attach 50 ทวิ" clicks it (see attachWht).
  // ⛔ Up here with the other hooks: below the `notApplicable` early return it is a conditional hook
  // (react-hooks/rules-of-hooks — the production build refuses to compile it).
  const whtInputs = useRef<Record<string, HTMLInputElement | null>>({});
  // The stacking windows, oldest first: see `stack` at the bottom. Above the `notApplicable` return, as this is a hook too.
  const windowOrder = useRef<string[]>([]);
  // Batch 12 tail T3 (FS-11): the family's column sort — the server's whitelist, the viewer's stored preference read on
  // mount (so the first request carries it), the page back to 1 on a change. ⛔ Above the `notApplicable` return too.
  const ls = useListSort(ctx, `sales.${kind}` as ListId, { onChange: () => setPage(1) });
  const { sort: sortK, dir: sortD } = sortArgs(ls);
  const modalKind: 'quotation' | 'invoice' = kind === 'quotation' ? 'quotation' : 'invoice';
  // ⛔ The two read-only seats (the books login, the auditor) are not offered a control the server
  // will refuse. The SERVER is the gate — middleware.ts refuses every non-GET from a books login and
  // keeps the auditor off /api/acct-workspace altogether — and hiding the buttons is only so that a
  // read-only reader is never shown a form whose only possible answer is a 403 (which, with a JSON
  // body, is also the shape that pages the admins).
  const readOnly = ctx.me.role === 'books' || ctx.me.role === 'auditor';
  const canCreate = !readOnly;
  // One page per note type (owner 2026-11-02, "Separate Credit Notes and Debit Notes"); both still issue through POST /docs/note.
  const isNote = kind === 'credit_note' || kind === 'debit_note';
  const canEdit = !readOnly && !isNote;
  // ⛔ The VAT flag is the MERCHANT row's own (profile.vat_registered) — the flag calcItems prices
  // by — never clients.has_vat, which is the firm's filing whitelist and is allowed to diverge
  // (review 2026-10-15: a has_vat mismatch silently issued zero-VAT tax invoices). The dialogs only
  // render once the profile is loaded, so deriving from it is always defined there.
  const vatReg = !!profile?.vat_registered;
  const docApi: MerchantReceiptApi = useMemo(() => ({
    customers: (page?: number, per?: number, q?: string) => api.awCustomers(ctx.clientId, page, per, q),
    saveCustomer: (_id: string | null, b: any) => api.awSaveCustomer(ctx.clientId, b),   // the picker only creates
    products: () => api.awProducts(ctx.clientId),
    createInvoice: (b: any) => api.awCreateDoc(ctx.clientId, kind === 'tax_invoice' ? 'tax_invoice' : 'invoice', b),
    createQuotation: (b: any) => api.awCreateDoc(ctx.clientId, 'quotation', b),
    updateDoc: (k: 'invoice' | 'quotation', id: string, b: any) => api.awUpdateDoc(ctx.clientId, k, id, b),
    uploadDocImage: (f: File) => api.awUploadDocImage(ctx.clientId, f),
    docImageBlob: (url: string) => api.awDocImageBlob(ctx.clientId, url),
    docs: (k: 'invoice' | 'receipt', page?: number, per?: number, q?: string) => api.awPortalDocs(ctx.clientId, k, page, per, q),
    createNote: (b: any) => api.awCreateDoc(ctx.clientId, 'note', b),
    noteSource: (k: 'invoice' | 'receipt', id: string) => api.awNoteSource(ctx.clientId, k, id),
    notePreflight: (b: any) => api.awNotePreflight(ctx.clientId, b),
    saleAccounts: () => api.awProductAccounts(ctx.clientId),   // the line chip: where a picked code books
    // The published month-end rate, shown beside the one somebody types on a foreign-currency
    // document. Staff only, like saleAccounts: /accounting-tax is behind the bookkeeping module.
    fxHint: (currency: string, ym: string) => api.acctFxMonthEnd(currency, ym),
    // The receipt family (FS-4): the portal's three calls, each to its workspace twin. The PATCH is
    // the same door updateDoc uses, on 'receipt' — the server writes remark + bank account and nothing
    // else (portalDocs.ts docPatchRoute), whichever side of the login asked.
    unpaidInvoices: (q?: string, id?: string) => api.awUnpaidInvoices(ctx.clientId, q || '', id || ''),
    createReceipt: (b: any) => api.awCreateDoc(ctx.clientId, 'receipt', b),
    updateReceipt: (id: string, b: any) => api.awUpdateDoc(ctx.clientId, 'receipt', id, b),
    receiptPreflight: (invoiceId: string, date?: string) => api.awReceiptPreflight(ctx.clientId, invoiceId, date || ''),
    createDocGate: (ask: { docType: string; date: string; currency: string; fxRate: number; total: number }) => api.awCreateDocGate(ctx.clientId, ask),
    editDate: (k: string, id: string, date: string) => api.awDocEditDate(ctx.clientId, k, id, date),
    editableNow: (k: string, id: string) => api.awDocEditable(ctx.clientId, k, id),
    taxForCandidates: (customerId: string) => api.awTaxForCandidates(ctx.clientId, customerId),
    taxForDate: (forId: string, date: string) => api.awTaxForDate(ctx.clientId, forId, date),
    quotationImpact: (id: string, ask: { date: string; currency: string; fxRate: number; total: number; forDocId?: string }) => api.awQuotationImpact(ctx.clientId, id, ask),
    quotationBills: (customerId: string) => api.awQuotationBills(ctx.clientId, customerId),
    copyGate: (id: string, ask: { customerId: string; date: string; currency: string; fxRate: number; total: number }) => api.awCopyGate(ctx.clientId, kind, id, ask),
    evidence: (k: string, id: string) => <RecordEvidence clientId={ctx.clientId} kind={k} id={id} />,
  }), [ctx.clientId, kind]);
  // The Send Email window (owner 2026-11-02, MerchantDoc/SendDocModal.tsx): the portal's window against the workspace's doors. The
  // verdict that opened it may be minutes old, so `preflight` reads it again at the Send click, as endDoc below does.
  const sendApi: SendDocApi = useMemo(() => ({
    draft: (k: string, id: string) => api.awSendDraft(ctx.clientId, k, id),
    send: (k: string, id: string, b: any) => api.awSendDoc(ctx.clientId, k, id, b),
    openPdf: async (k: string, id: string) => {
      try {
        // As Open PDF asks below: the window may have stood open for minutes.
        const v: AwDocActions = await api.awDocActions(ctx.clientId, k, id);
        if (!v.found) { window.alert(t('aw_doc_menu_gone')); return; }
        openBlob(await api.awDocPdfBlobUrl(ctx.clientId, k, id));
      }
      catch (e: any) { window.alert(e?.message || t('p_pdf_failed')); }
    },
    preflight: async (k: string, id: string) => {
      const v: AwDocActions = await api.awDocActions(ctx.clientId, k, id);
      if (!v.found) return t('aw_doc_menu_gone');
      const s = v.actions.send;
      return s && !s.allowed ? sendWhy(t, s) : '';
    },
  }), [ctx.clientId, t]);
  // The dialogs read the merchant's world from the portal profile shape; fetched once per client,
  // only when a dialog is actually about to open.
  const needProfile = async (): Promise<any> => {
    if (profile) return profile;
    const pr: any = await api.awDocProfile(ctx.clientId);
    // The merchant's own region, keyed by ITS company id, so the shared dialog's home currency and
    // calendar day (merchantToday/currencyCode) answer for the merchant, not the firm. ONLY the
    // per-company map is touched — never the portal's global overrides (setRegionOverride,
    // setCurrencyWords), which belong to the staff shell here, and never clearCompanyRegions(),
    // which would wipe the staff map the shell already loaded.
    if (pr?.id && pr?.docRegion) addCompanyRegions({ [pr.id]: pr.docRegion });
    setProfile(pr);
    return pr;
  };
  const openCreate = async () => {
    try { await needProfile(); setCreateOpen(true); } catch (e: any) { setErr(e.message); }
  };
  // The server decides whether a document may be edited — the same rules that guard the PATCH — so
  // the button can never offer something the law (or a linked receipt) forbids. Mirrors the
  // portal's own openEdit to the letter, alerts and all.
  const openEdit = async (d: AwDoc) => {
    if (busyEdit) return; setBusyEdit(d.id);
    try {
      const v: any = await api.awDocEditable(ctx.clientId, kind, d.id);
      if (!v.editable) {
        // Batch 11 R3 (W2-14): a closed period is answered before the window opens, in the words its PATCH used to refuse with.
        window.alert(v.code === 'closed' && v.lock?.period
          ? t('p_doc_err_period_closed_edit', { docNo: v.docNo, date: dmy(d.date), from: dmy(v.lock.period.start), to: dmy(v.lock.period.end) })
          : v.billedNos ? t('aw_doc_menu_edit_billed', { nos: v.billedNos })
          : (v.reason || t('p_doc_not_editable')));
        return;
      }
      if (v.warnHandedOver && !window.confirm(t('p_doc_confirm_handed_over', { docNo: v.docNo }))) return;
      const [pr, full] = [await needProfile(), await api.awDoc(ctx.clientId, kind, d.id)];
      void pr;
      setSel(null);
      // Batch 11 R3 (W2-3): in the books → the LIMITED window (remark, bank account, payment method, due date).
      setEditDoc({ ...full, __needsReason: !!v.needsReason, __limited: v.limited === 'books' ? (v.lock || { code: 'in_books', entries: v.inBooks || [] }) : null,
        // (W2 ship) a receipt / a note / a filed month keeps the rest as it is whatever is reversed: the window says so.
        __fullBlocked: v.fullEditBlocked ? String(v.fullEditBlocked.code || '') : '',
        // (COA-D6, b14 review) a tax invoice for a paid invoice whose sale has changed: what the window says it is for.
        __fullBlockedParams: v.fullEditBlocked?.params || null });
    } catch (e: any) { window.alert(e?.message || t('p_doc_err_open')); }
    finally { setBusyEdit(''); }
  };
  // A successful save closes THAT window only. Nothing traps focus inside a full-screen window, so the
  // list's New / Edit stay reachable by keyboard under it and two windows can be open at once; one shared
  // "done" closed both, and saving one threw the other's typing away unasked (break-it review 2026-10-20).
  const closeCreate = () => { setCreateOpen(false); setConvert(null); };
  const doneCreate = () => { closeCreate(); setBump(n => n + 1); };
  const doneEdit = () => { setEditDoc(null); setBump(n => n + 1); };

  // Thai tax documents only exist for a Thai-mode merchant. For any other the shell does not list the
  // submenu at all (menus.ts thaiOnly / visibleSubs — owner 2026-10-13: no grey menus), so this branch is
  // reached only in the moment between /summary arriving and the shell re-resolving the URL: the list is
  // not fetched and a plain line says why — never the grey "not in this version" card.
  const thaiOnly = kind === 'tax_invoice' || isNote;
  const notApplicable = thaiOnly && !!ctx.summary && !ctx.summary.thaiTaxMode;

  // The last row of the last page voided: back to the page that still exists, never an empty table while rows remain.
  useEffect(() => {
    if (!data) return;
    const pages = Math.max(1, Math.ceil(data.total / (data.limit || limit)));
    if (page > pages) setPage(pages);
  }, [data, limit, page]);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    if (notApplicable) return;
    let alive = true;
    api.awDocs(ctx.clientId, kind, { page, limit, q: qd, sort: sortK, dir: sortD, voided: showVoided })
      .then((d: AwDocPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, kind, page, limit, qd, ctx.reload, notApplicable, bump, sortK, sortD, showVoided]);

  if (notApplicable) return <div style={note}><i className="ti ti-info-circle" aria-hidden /> <b>{title}</b> — {t('aw_thai_only')}</div>;

  // ⛔ The PDF's preflight, asked at the click, of every seat (owner 2026-11-02; an exception to "a read-only seat reads no verdict",
  // below, which stays true of the menu's opening): a row gone since the list was read answers /pdf a 404 with a JSON body, which pages
  // the admins, and the document window and the Send window ask from here too, however long they have stood open. Nothing is rendered
  // when it says the row has gone, or cannot say.
  const stillThere = async (d: AwDoc): Promise<boolean> => {
    const v: AwDocActions = await api.awDocActions(ctx.clientId, kind, d.id);
    if (!v.found) setErr(t('aw_doc_menu_gone'));
    return !!v.found;
  };
  const openPdf = async (d: AwDoc) => {
    if (busy) return; setBusy(d.id); setErr('');
    try { if (await stillThere(d)) openBlob(await api.awDocPdfBlobUrl(ctx.clientId, kind, d.id)); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // The same render as Open PDF, saved as <doc_no>.pdf; the trail says which it was (acctWorkspace.ts /pdf).
  const downloadPdf = async (d: AwDoc) => {
    if (busy) return; setBusy(d.id); setErr('');
    try { if (await stillThere(d)) await api.awDocPdfDownload(ctx.clientId, kind, d.id, d.doc_no); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // The buyer's 50 ทวิ, attached the portal's way: a hidden file input per receipt row, the menu item
  // clicks it, the portal's own handler stores it (FS-4). The receipt form tells the operator to do
  // exactly this "afterwards from the receipt list", so the list has to offer it.
  // ⛔ Its preflight, here at the pick (owner 2026-11-02): the upload's limit (portalDocs.ts `upload`) and the row, which the verdict
  // that offered the item said was live before the file dialog stood open for however long.
  const attachWht = async (d: AwDoc, f: File | undefined) => {
    if (!f) return;
    if (f.size > WHT_MAX_BYTES) { window.alert(t('aw_doc_wht_too_big')); return; }
    try {
      const v: AwDocActions = await api.awDocActions(ctx.clientId, kind, d.id);
      if (!v.found) { window.alert(t('aw_doc_menu_gone')); return; }
      await api.awUploadWht(ctx.clientId, d.id, f); window.alert(t('p_doc_wht_saved'));
    }
    catch (e: any) { window.alert(e?.message || t('p_err_save_failed')); }
  };

  // ── Void and Delete (owner 2026-11-02, "Consolidated Document Actions Dropdown") ─────────────────────────────────────
  // The window a reason is typed in; the verdict that opened it may be minutes old by then, so it is asked again at the click:
  // a refused POST pages the admins, and a sentence here is what the reader needs anyway. The row gone meanwhile is the end
  // this window was for. The page, the search and the sort stay: the list is read again as it stands (ctx.bump).
  const endDoc = async (e: { d: AwDoc; door: 'void' | 'delete' }, reason: string) => {
    const v: AwDocActions = await api.awDocActions(ctx.clientId, kind, e.d.id);
    if (v.found) {
      if (e.door === 'delete' && !v.actions.delete) throw new Error(t('aw_doc_end_no_delete', { docNo: e.d.doc_no }));
      if (e.door === 'void' && v.actions.void && !v.actions.void.allowed) throw new Error(endWhy(t, v.actions.void.refusal, v.actions.void.entries) || v.actions.void.reason);
      try {
        await (e.door === 'void' ? api.awVoidDoc : api.awReleaseDoc)(ctx.clientId, kind, e.d.id, reason);
      } catch (x: any) {
        throw new Error((x?.payload && endWhy(t, x.payload)) || x?.message || t('p_err_save_failed'));
      }
    }
    setEnding(null);
    if (sel?.id === e.d.id) setSel(null);
    ctx.bump();
  };

  const isReceipt = kind === 'receipt';
  // The Notes lists too (round 4): a debit note is owed and collected like a bill; a credit note never is (—).
  const showOwing = kind === 'invoice' || kind === 'tax_invoice' || isNote;
  // Two families mix two doc_types each (invoice + proforma, tax invoice + receipt/tax invoice), so a row of the other type says which
  // it is — a proforma must never read as a bill — in small text under its number (owner 2026-11-02, the seven columns); a row with no
  // such text is what the page's own name says. Each notes page is one type (owner 2026-11-02).
  const showType = kind === 'invoice' || kind === 'tax_invoice';
  const rows = data?.rows || [];

  // ── A quotation's Status dropdown (owner 2026-11-02, "Quotation Status Management & Document Conversion", services/quotationStage.ts) ────────────
  // Awaiting, Issued, Rejected, in the reader's language, on the verdict read as it opens: what stands in the way is greyed with why, which is what keeps a
  // refused POST from paging the admins. The one it holds is ticked. Rejected asks for its one line in a window; the other two are a click, and neither
  // sets `edited`. The conversion group is below (convertMenu). A voided row and a seat that may not write have a plain pill.
  const setOffer = async (d: AwDoc, status: AwOfferStatus, reason = '') => {
    // Asked again at the click, as endDoc does: the verdict that offered it may be minutes old by now, and a window may have stood open longer.
    const v: AwDocActions = await api.awDocActions(ctx.clientId, kind, d.id);
    if (!v.found) throw new Error(t('aw_doc_menu_gone'));
    const o = v.actions.offer;
    if (status === 'rejected' && o && !o.rejected.allowed) throw new Error(endWhy(t, o.rejected.refusal) || o.rejected.reason);
    if (status === 'awaiting' && o && !o.awaiting.allowed) throw new Error(t('aw_offer_held_issued', { nos: o.awaiting.nos }));
    try { await api.awSetOfferStatus(ctx.clientId, d.id, status, reason); }
    catch (x: any) { throw new Error((x?.payload && endWhy(t, x.payload)) || x?.message || t('p_err_save_failed')); }
    setRejecting(null);
    ctx.bump();
  };
  const stageMenu = (d: AwDoc, v: AwDocActions | null, readError: string): RowMenuEntry[] => {
    if (readError) return [{ key: 'failed', note: readError }];
    if (!v) return [{ key: 'reading', note: t('aw_list_reading') }];
    if (!v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const o = v.actions.offer;
    if (!o) return [{ key: 'none', note: t('aw_list_status_no_door') }];
    const change = (status: AwOfferStatus) => () => { setErr(''); setOffer(d, status).catch((e: any) => setErr(String(e?.message || e))); };
    const word = (status: AwOfferStatus) => salesStatus(t, 'quotation', status).word;
    const entry = (status: AwOfferStatus, icon: string, withheld: string | undefined, onPick: () => void, danger = false): RowMenuEntry => (o.status === status
      // Picking what it already holds changes nothing and closes the menu, as a select does.
      ? { key: `offer_${status}`, icon: 'ti-check', label: word(status), attrs: { 'aria-current': 'true' }, onPick: () => undefined }
      : { key: `offer_${status}`, icon, label: word(status), withheld, onPick, danger });
    return [
      entry('awaiting', 'ti-clock', o.awaiting.allowed ? undefined : t('aw_offer_held_issued', { nos: o.awaiting.nos }), change('awaiting')),
      entry('issued', 'ti-circle-check', undefined, change('issued')),
      ...(v.actions.convert ? convertMenu(d, v.actions.convert) : []),
      entry('rejected', 'ti-circle-x', o.rejected.allowed ? undefined : (endWhy(t, o.rejected.refusal) || o.rejected.reason), () => { setErr(''); setRejecting(d); }, true),
    ];
  };
  // ── Its conversion group (owner 2026-11-02, the requirement's section 2) ────────────────────────────────────────────────────────────────────
  // The server's verdict alone says what is offered and what is greyed with why (portalDocs.ts quotationConvertVerdict): which destinations this kind of company has, the live documents billing it
  // with their numbers and statuses, and a date or currency the form could still change as a hover, not a grey. "Create Invoice / Tax Invoice" is merged into Create Tax Invoice (the owner's
  // ruling): the system has no such type. Create Receipt is the bill's receipt where a bill owes something, else the cash-sale form where the company has one.
  const convertMenu = (d: AwDoc, c: AwConvertVerdict): RowMenuEntry[] => {
    const why = (a: AwConvertAnswer) => (a.allowed ? undefined : (endWhy(t, a.refusal) || a.reason));
    const soft = (a: AwConvertAnswer, title: string) => (a.allowed && a.gate ? (endWhy(t, a.gate) || String(a.gate.error || '')) : title);
    const out: RowMenuEntry[] = c.bills.map(b => ({ key: `conv_doc:${b.docNo}`, note: <>
      <b style={{ color: 'var(--text-primary)' }}>{b.docNo}</b> · {DT[b.docType] ? t(DT[b.docType]) : b.docType} · {salesStatus(t, b.docType, b.status).word}
      {b.forNo ? <> · {t('aw_conv_doc_for', { docNo: b.forNo })}</> : b.outstanding > 0.005 ? <> · {t('aw_col_outstanding')}: {amt(b.outstanding)}</> : null}
    </> }));
    for (const no of c.freed) out.push({ key: `conv_freed:${no}`, note: t('aw_conv_doc_freed', { docNo: no }) });
    if (c.bankMatched.length) {
      const sum = c.bankMatched.reduce((a, l) => a + l.amount, 0);
      out.push({ key: 'conv_bank', note: t(c.bankMatched.length > 1 ? 'aw_conv_bank_matched_n' : 'aw_conv_bank_matched', { n: c.bankMatched.length, date: dmy(c.bankMatched[0].date), amount: amt(sum) }) });
    }
    if (c.receipts.length) out.push({ key: 'conv_receipts', note: t('p_conv_receipts', { list: c.receipts.join(', ') }) });
    const item = (key: string, icon: string, label: string, a: AwConvertAnswer, onPick: () => void, title: string): RowMenuEntry => ({ key, icon, label, withheld: why(a), title: why(a) ? undefined : soft(a, title), onPick, attrs: { 'data-conv-item': key } });
    if (c.offered.includes('invoice')) out.push(item('conv_invoice', 'ti-file-invoice', t('aw_conv_create_invoice'), c.dest.invoice, () => openConvert(d, 'invoice'), t('aw_conv_books_invoice')));
    if (c.offered.includes('tax_invoice')) {
      const a = c.dest.tax_invoice;
      // Several paid invoices of one quotation, standing from before a second billing was refused: one entry for each, never chosen for the reader.
      if (a.options?.length) for (const o of a.options) out.push(item(`conv_tax_for:${o.docNo}`, 'ti-file-certificate', t('aw_conv_create_tax_for', { docNo: o.docNo }), { ...a, allowed: true, gate: null }, () => openConvert(d, 'tax_invoice', o.id), t('aw_conv_books_tax_invoice')));
      else out.push(item('conv_tax_invoice', 'ti-file-certificate', t('aw_conv_create_tax_invoice'), a, () => openConvert(d, 'tax_invoice'), a.forId ? t('p_cdoc_taxfor_note', { docNo: a.forDocNo, receiptNos: (a.receiptNos || []).join(', '), paid: amt(a.paid), paidVat: amt(a.paidVat) }) : t('aw_conv_books_tax_invoice')));
    }
    if (c.offered.includes('receipt')) {
      const r = c.dest.receipt, cash = c.dest.tax_invoice_receipt;
      if (r.allowed && (r.on || []).length > 1) for (const b of r.on || []) out.push(item(`conv_receipt:${b.docNo}`, 'ti-receipt', t('aw_conv_create_receipt_on', { docNo: b.docNo }), r, () => openConvert(d, 'receipt', b.id), t('aw_conv_receipt_title')));
      else if (r.allowed) out.push(item('conv_receipt', 'ti-receipt', t('aw_conv_create_receipt'), r, () => openConvert(d, 'receipt', r.on?.[0]?.id), t('aw_conv_receipt_title')));
      else if (!c.bills.length && cash.allowed) out.push(item('conv_receipt', 'ti-receipt', t('aw_conv_create_receipt'), cash, () => openConvert(d, 'tax_invoice_receipt'), t('aw_conv_books_cash')));
      else out.push(item('conv_receipt', 'ti-receipt', t('aw_conv_create_receipt'), r, () => undefined, ''));
    }
    return out;
  };
  // What a pick does: the verdict asked again (it may be minutes old), then the window, filled, with nothing saved. A window already open stays: nothing traps focus in it, and its typing is the reader's.
  const openConvert = async (d: AwDoc, dest: ConvertSeedType | 'receipt', pick = '') => {
    if (createOpen) return;
    setErr('');
    try {
      const v: AwDocActions = await api.awDocActions(ctx.clientId, kind, d.id);
      if (!v.found) { setErr(t('aw_doc_menu_gone')); return; }
      const c = v.actions.convert;
      if (!c) return;
      const a = c.dest[dest];
      const opt = dest === 'tax_invoice' && pick ? a.options?.find(o => o.id === pick) : undefined;
      if (!a.allowed && !opt) { setErr(endWhy(t, a.refusal) || a.reason); return; }
      const pr = await needProfile();
      if (dest === 'receipt') {
        const b = a.on?.find(x => x.id === pick) || a.on?.[0];
        if (!b) return;
        setConvert({ receiptOn: { id: b.id, docNo: b.docNo } });
      } else {
        const saleId = opt ? opt.id : a.forId || '';
        const doc: any = await api.awDoc(ctx.clientId, saleId ? 'invoice' : 'quotation', saleId || d.id);
        const q: any = saleId ? await api.awDoc(ctx.clientId, 'quotation', d.id) : doc;
        setConvert({ seed: buildConvertSeed({ doc, quotation: { id: d.id, doc_no: d.doc_no, valid_until: String(q.valid_until || '') }, docType: dest, verdict: c,
          forSale: saleId ? { id: saleId, docNo: opt ? opt.docNo : String(a.forDocNo || ''), receiptNos: (opt ? opt.receiptNos : a.receiptNos) || [], paid: Number(opt ? opt.paid : a.paid) || 0, paidVat: Number(opt ? opt.paidVat : a.paidVat) || 0, currency: a.currency } : null,
          // The window's own first day: the merchant's calendar outside Thailand.
          today: thaiModeOf(pr) ? todayISO() : merchantToday(pr?.id), plain: !thaiModeOf(pr), banks: pr?.banks || [] }) });
      }
      setCreateOpen(true);
    } catch (e: any) { setErr(String(e?.message || e)); }
  };

  // Copy (owner 2026-11-02, the requirement's section 3): the window, filled from this row, with nothing saved and no number taken until Issue, and the row itself never written to. The verdict is asked
  // again at the pick, as openConvert does, because the row may have gone. A window already open stays, for the same reason as there.
  const openCopy = async (d: AwDoc) => {
    if (createOpen) return;
    setErr('');
    try {
      const v: AwDocActions = await api.awDocActions(ctx.clientId, kind, d.id);
      if (!v.found) { setErr(t('aw_doc_menu_gone')); return; }
      const c = v.actions.copy;
      if (!c) return;
      const pr = await needProfile();
      const doc: any = await api.awDoc(ctx.clientId, kind, d.id);
      setConvert({ seed: buildCopySeed({ doc, kind: kind === 'quotation' ? 'quotation' : kind === 'tax_invoice' ? 'tax_invoice' : 'invoice', customer: c.customer,
        // The window's own first day: the merchant's calendar outside Thailand.
        today: thaiModeOf(pr) ? todayISO() : merchantToday(pr?.id), plain: !thaiModeOf(pr), banks: pr?.banks || [] }) });
      setCreateOpen(true);
    } catch (e: any) { setErr(String(e?.message || e)); }
  };

  // ── The row's actions, one ⋯ menu (owner 2026-11-02, "Consolidated Document Actions Dropdown") ──────────────────────
  // Replaces the strip of buttons with today's actions under today's conditions: no rule changes here. Edit is still the
  // seat's and the family's (canEdit), and openEdit still asks /editable at the click; what is new is that the server's
  // verdict is read as the menu opens, so an Edit that /editable would answer "no" is greyed with why instead of an alert
  // after the click. A read-only seat reads no verdict as the menu opens: nothing it is offered depends on one (its PDFs ask at the
  // click, stillThere above). Accounting entries and Attach 50 ทวิ are not in the requirement's list, but taking them away would lose
  // what the row does, so they stay.
  const rowMenu = (d: AwDoc, v: AwDocActions | null, readError: string): RowMenuEntry[] => {
    if (v && !v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const items: RowMenuEntry[] = [];
    if (canEdit) {
      const e = v?.actions.edit;
      const shut = e && !e.allowed ? e : null;
      items.push({
        key: 'edit', icon: 'ti-pencil', label: t('p_doc_act_edit'), onPick: () => openEdit(d),
        waiting: (!v && !readError) || !!busyEdit,
        withheld: readError || (shut ? (
          // Batch 11 R3 (W2-14): a closed period, in the words its PATCH used to refuse with, as openEdit says it.
          shut.code === 'closed' && shut.period ? t('p_doc_err_period_closed_edit', { docNo: v?.docNo, date: dmy(d.date), from: dmy(shut.period.start), to: dmy(shut.period.end) })
            : shut.code === 'billed' ? t('aw_doc_menu_edit_billed', { nos: shut.nos })
            : shut.code === 'receipts' ? t(kind === 'tax_invoice' ? 'aw_doc_menu_edit_receipts_tax' : 'aw_doc_menu_edit_receipts')
            : EDIT_WITHHELD_KEY[shut.code] ? t(EDIT_WITHHELD_KEY[shut.code])
            : (shut.reason || t('p_doc_not_editable'))
        ) : undefined),
      });
    }
    items.push({ key: 'open_pdf', icon: 'ti-file-type-pdf', label: t('aw_col_open_pdf'), onPick: () => openPdf(d), waiting: !!busy });
    items.push({ key: 'download_pdf', icon: 'ti-download', label: t('aw_doc_menu_download_pdf'), onPick: () => downloadPdf(d), waiting: !!busy });
    // Round 10: where the requirement's list has it, on the families that have one, and never to a seat that may not write. The server says which rows (portalDocs.ts docCopyVerdict): where it says none, no item.
    if (canCreate && !isNote && kind !== 'receipt' && (!v || v.actions.copy)) {
      items.push({ key: 'copy', icon: 'ti-copy', label: t('aw_doc_menu_copy'), title: t('aw_doc_menu_copy_title'), onPick: () => openCopy(d), waiting: !v && !readError, withheld: readError || undefined });
    }
    // Round 6: all six families, wherever the portal offers it, and never to a seat that may not write. Greyed with why when the
    // client company or the workspace is missing what a send needs; a customer with no address is the window's to say.
    if (!readOnly && (!v || v.actions.send)) {
      const s = v?.actions.send;
      const shut = s && !s.allowed ? s : null;
      items.push({
        key: 'send', icon: 'ti-mail-forward', label: t('aw_doc_menu_send'), onPick: () => setSending(d),
        waiting: !v && !readError, withheld: readError || (shut ? sendWhy(t, shut) : undefined),
        title: shut ? undefined : t(s?.hasCustomerEmail === false ? 'aw_doc_menu_send_no_customer' : 'aw_doc_menu_send_title'),
      });
    }
    // COA-D2 — from the list as well as from the document window, the same way Open PDF is offered in both places. The
    // kind is the row's own doc_type: a proforma inside the Invoices family asks as a proforma.
    if (postsEntries(kind)) items.push({ key: 'entries', icon: 'ti-book-2', label: t('aw_ef_title'), title: t('aw_ef_btn_title'), onPick: () => setEntriesOf(d), attrs: { 'data-ef-btn': d.doc_type || kind } });
    // A plain receipt records no withholding, so there is no certificate to attach — the portal hides it on the same test,
    // the row's own stored mode (which also decides whether the row has the input); the verdict, read fresh, can only take it away.
    if (isReceipt && !readOnly && !docPlainOf(d) && (!v || !!v.actions.attach_wht?.allowed)) {
      items.push({ key: 'attach_wht', icon: 'ti-paperclip', label: t('p_doc_act_attach_wht'), title: t('p_doc_act_attach_wht_title'), onPick: () => whtInputs.current[d.id]?.click(),
        waiting: !v && !readError, withheld: readError || undefined });
    }
    return [...items, ...endItems(d, v)];
  };
  // Round 5: the server's one item for this row, never worked out from its status (a booked tax invoice reads 'draft'). It
  // is not drawn until the verdict says which it is, and a read-only seat, which reads none, never sees it. A document in
  // the books says why and offers the existing way on: the entries panel on the entry to reverse, as a refusal's "Reverse
  // JV-…" does (reverseEntryBtn.tsx); an entry in a closed period is not reversed, so it is not offered.
  const endItems = (d: AwDoc, v: AwDocActions | null): RowMenuEntry[] => {
    const items: RowMenuEntry[] = [];
    const del = v?.actions.delete;
    const vo = v?.actions.void;
    if (del) items.push({ key: 'delete', icon: 'ti-trash', danger: true, label: t('aw_doc_menu_delete'), title: t('aw_doc_menu_delete_title'), onPick: () => setEnding({ d, door: 'delete' }) });
    if (vo) {
      const refused = !vo.allowed;
      // The hover says why this row has no Delete, which its 'draft' pill alone does not (there is no real Draft state, the owner's ruling).
      items.push({ key: 'void', icon: 'ti-ban', danger: true, label: t('p_doc_act_void'), withheld: refused ? (endWhy(t, vo.refusal, vo.entries) || vo.reason) : undefined, title: refused ? undefined : `${t('aw_doc_menu_void_title')} ${t('aw_doc_menu_no_delete_title')}`, onPick: () => setEnding({ d, door: 'void' }) });
      if (refused && vo.code === 'in_books') {
        for (const e of vo.entries || []) {
          if (!e.entryId || (e.closed && !e.reversalDraft)) continue;
          items.push({ key: `reverse:${e.entryId}`, icon: 'ti-arrow-back-up', label: e.reversalDraft ? t('aw_ll_open_reversal', { rv: e.reversalDraft }) : t('aw_ll_reverse', { docNo: e.docNo || e.entryId }), onPick: () => setEntriesOf({ ...d, focusEntryId: e.entryId }) });
        }
      }
    }
    return items;
  };
  // ── The row's Status dropdown (owner 2026-11-02, the requirement's section 5: "Keep the Status column as a dropdown, with options
  // appropriate to each document type") ──────────────────────────────────────────────────────────────────────────────────────────
  // ⛔ PAID AND PARTIAL ARE NEVER PICKED HERE: they are derived from what was received (db/index.ts). What a person does to a sales document's
  // status is end it, so that is what is offered, the very entries and windows the ⋯ has, on the verdict read as this opens. Not on a
  // quotation: its own dropdown is above. Not on a voided row, and not for a seat that may not write: those are plain pills.
  const statusMenu = (d: AwDoc, v: AwDocActions | null, readError: string): RowMenuEntry[] => {
    if (readError) return [{ key: 'failed', note: readError }];
    if (!v) return [{ key: 'reading', note: t('aw_list_reading') }];
    if (!v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const doors = endItems(d, v);
    return doors.length ? doors : [{ key: 'none', note: t('aw_list_status_no_door') }];
  };

  // ── The windows that stack (owner 2026-11-02) ──────────────────────────────────────────────────────────────────────────────
  // Nothing traps focus inside a full-screen window, so the list's ⋯ stays reachable by keyboard under it (testFsTail, testUnsavedGuard),
  // and any of these can open over any other. They all sit on one layer, where the later element paints on top and the later mounted
  // answers Esc (utils/escLayer.ts). So the one opened LAST is drawn last, whichever it is, or it would open out of sight and take the
  // visible window's Esc. Those still open keep their order, so no live window's DOM moves. `sel` stays out: it needs a mouse on the list.
  const windows: Record<string, React.ReactNode> = {
    entries: entriesOf && <DocEntriesDialog ctx={ctx} kind={entriesOf.doc_type || kind} id={entriesOf.id} docNo={entriesOf.doc_no} focusEntryId={entriesOf.focusEntryId} onClose={() => setEntriesOf(null)} />,
    ending: ending && (
      <ReasonDialog title={t(ending.door === 'void' ? 'aw_doc_void_title' : 'aw_doc_delete_title')} docNo={ending.d.doc_no}
        line={<><b style={{ color: 'var(--text-primary)' }}>{ending.d.doc_no}</b> · {ending.d.client_name} · {amt(kind === 'receipt' ? ending.d.net_to_pay : ending.d.total_amount)} {ending.d.currency}</>}
        hint={t(ending.door === 'delete' ? 'aw_doc_delete_hint' : (kind === 'quotation' || kind === 'invoice' ? 'aw_doc_void_hint' : 'aw_doc_void_hint_tax'))}
        icon={ending.door === 'void' ? 'ti-ban' : 'ti-trash'} label={ending.door === 'void' ? t('p_doc_act_void') : t('aw_doc_menu_delete')}
        onClose={() => setEnding(null)} onSubmit={reason => endDoc(ending, reason)} />
    ),
    sending: sending && <SendDocModal kind={kind} doc={sending} sendApi={sendApi} layer={WORKSPACE_LAYER} onClose={() => setSending(null)} onSent={() => setBump(n => n + 1)} />,
    rejecting: rejecting && (
      <ReasonDialog title={t('aw_offer_reject_title')} docNo={rejecting.doc_no}
        line={<><b style={{ color: 'var(--text-primary)' }}>{rejecting.doc_no}</b> · {rejecting.client_name} · {amt(rejecting.total_amount)} {rejecting.currency}</>}
        hint={t('aw_offer_reject_hint')} icon="ti-circle-x" label={t('aw_offer_reject_label')}
        onClose={() => setRejecting(null)} onSubmit={reason => setOffer(rejecting, 'rejected', reason)} />
    ),
    /* ⛔ `fullscreen` ONLY here, never on the portal's own five call sites. The shell offers it
       per caller precisely so the two sides can differ: this is the staff workspace's document
       window (FS-1/FS-3), while a portal customer's screen must not change shape under them.
       The modals already accepted and forwarded the prop — nobody was passing it, so the
       full-screen window existed and no person could reach it.
       `layer` too: the host's stacking layer (MerchantDoc/shell.tsx). The staff windows are the
       workspace's, so they sit on WORKSPACE_LAYER — at the portal's default 50 a full-screen window
       would sit UNDER the workspace's toasts. */
    create: createOpen && profile && (
      isNote ? (
        <CreateNoteModal vatReg={vatReg} merchantId={profile?.id} docApi={docApi} fixedType={kind} fullscreen layer={WORKSPACE_LAYER}
          onClose={closeCreate} onDone={doneCreate} />
      ) : kind === 'receipt' || convert?.receiptOn ? (
        /* FS-4: the receipt dialogs the portal opens, with the portal's own two props — the merchant
           (whose region prints the amounts) and its document world (plain outside Thailand). */
        <CreateReceiptModal merchantId={profile?.id || null} plain={!thaiModeOf(profile)} docApi={docApi} fullscreen layer={WORKSPACE_LAYER} audience="staff" presetInvoice={convert?.receiptOn}
          onClose={closeCreate} onDone={doneCreate} />
      ) : (
        <CreateDocModal kind={convert?.seed && !convert.seed.copyOf ? 'invoice' : modalKind} vatReg={vatReg} profile={profile} docApi={docApi} fullscreen layer={WORKSPACE_LAYER} audience="staff" seed={convert?.seed}
          defaultDocType={kind === 'tax_invoice' ? 'tax_invoice' : undefined}
          onClose={closeCreate} onDone={doneCreate} />
      )
    ),
    edit: editDoc && profile && (
      kind === 'receipt' ? (
        <EditReceiptModal doc={editDoc} profile={profile} docApi={docApi} fullscreen layer={WORKSPACE_LAYER} audience="staff"
          onClose={() => setEditDoc(null)} onDone={doneEdit} />
      ) : (
        <CreateDocModal kind={modalKind} vatReg={vatReg} profile={profile} existing={editDoc} docApi={docApi} fullscreen layer={WORKSPACE_LAYER} audience="staff"
          inBooksAction={r => <ReverseEntryBtn ctx={ctx} kind={editDoc.doc_type || kind} id={editDoc.id} docNo={editDoc.doc_no} entry={r} style={{ ...btn, padding: '2px 8px', fontSize: 11.5 }} />}
          onClose={() => setEditDoc(null)} onDone={doneEdit} />
      )
    ),
  };
  const openWindows = Object.keys(windows).filter(k => !!windows[k]);
  windowOrder.current = [...windowOrder.current.filter(k => openWindows.includes(k)), ...openWindows.filter(k => !windowOrder.current.includes(k))];
  const stack = windowOrder.current.map(k => <React.Fragment key={k}>{windows[k]}</React.Fragment>);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_search_ph')} style={{ ...inp, flex: '1 1 240px', maxWidth: 420 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
        <div style={{ flex: 1 }} />
        {/* (Owner 2026-11-02, the seven columns) Outstanding left the headers for the Status cell's small text, and this is still the way to
            see what is owed most, on the lists that owe. Sorting is a GET, so every seat has it. */}
        {(kind === 'invoice' || kind === 'tax_invoice' || kind === 'debit_note') && <SortChip k="outstanding" s={ls} label={t('aw_col_outstanding')} />}
        {/* Page 1 on a change, as a new search is; no seat is kept from reading what was voided. */}
        <label data-show-voided="" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={showVoided} onChange={e => { setShowVoided(e.target.checked); setPage(1); }} />
          {t('p_flt_show_voided')}
        </label>
        {/* Every row of THIS family, with the search the list is showing — not the page. */}
        <DlBtn style={btn} title={t('aw_export_title')} onRun={() => api.awDocsExport(ctx.clientId, kind, { q, sort: sortK, dir: sortD, voided: showVoided })}>
          <i className="ti ti-file-spreadsheet" aria-hidden /> {t('aw_export')}
        </DlBtn>
        {/* ➕ New, beside Export (owner 2026-11-02: this returns to where it stood before batch 12 tail T2, TL-14 moved it
            to the Fab; NewDocBtn.tsx): the same openCreate, the same condition (a seat that writes, a client with its
            merchant row), its title the same words — never for a read-only seat. */}
        {canCreate && !!ctx.summary?.hasMerchant && <NewDocBtn onClick={openCreate} title={t('aw_doc_new')} />}
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                {/* Seven columns (owner 2026-11-02, "Standardized Document Table Layout"), on every family: what used to have a column of its
                    own is small text in a cell (listCells.tsx), its document's popover or its window, and exports keep them all. Each that
                    holds one value sorts (FS-11), on the server; Currency (one per merchant) and the actions do not, and a receipt's status
                    is always the one word, so it does not sort. A receipt's Total is what arrived (net_to_pay): its cell says so on hover. */}
                <SortTh k="doc_no" s={ls} label={t('aw_col_no')} />
                <SortTh k="date" s={ls} label={t('aw_col_date')} />
                <SortTh k="customer" s={ls} label={t('aw_col_customer')} />
                <SortTh k="total" s={ls} align="right" label={t('aw_col_total')} />
                <th style={th}>{t('aw_col_currency')}</th>
                {isReceipt ? <th style={th}>{t('aw_col_status')}</th> : <SortTh k="status" s={ls} label={t('aw_col_status')} />}
                <th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(d => {
                // A PROFORMA is not a receivable (owner 2026-10-02, the AR card's rule): nothing is owed on it, so its Status says no
                // figure, instead of a red one that the Dashboard › Receivables card in the same workspace rightly leaves out.
                const proforma = d.doc_type === 'proforma';
                // A credit note is money going back — never paid, never owed.
                const credit = d.doc_type === 'credit_note';
                // A voided row (Show voided, owner 2026-11-02; the project's convention: the row stays, struck): nothing is owed on it,
                // its Status says who, when and why, and it has no ⋯ at all. The portal's own void row has no action either.
                const gone = d.status === 'void';
                // A Rejected quotation reads greyed, with its pill in red (owner 2026-11-02): it stands, and may be changed back, so it is not struck.
                const offer = kind === 'quotation' && !gone ? d.offer : undefined;
                const rejected = offer?.status === 'rejected';
                const strike: React.CSSProperties = gone ? STRUCK : rejected ? GREYED : {};
                const noFigure = proforma || credit || gone;
                const o = showOwing && !noFigure ? owing(d) : 0;
                const st = salesStatus(t, d.doc_type || kind, offer ? offer.status : d.status);
                return (
                  // The whole row opens the document's lines; only the menu at the end acts
                  // (owner 2026-10-15: "click a row = the detail; the Edit button = the edit form").
                  <tr key={d.id} onClick={() => setSel(d)} style={{ cursor: 'pointer' }} {...(gone ? { 'data-doc-void': d.doc_no } : {})} {...(rejected ? { 'data-doc-rejected-row': d.doc_no } : {})}>
                    <NoCell no={d.doc_no} strike={gone} grey={rejected} sub={showType && d.doc_type !== kind && DT[d.doc_type] ? t(DT[d.doc_type]) : undefined}
                      lead={kind === 'quotation' ? <OfferDot ctx={ctx} id={d.id} docNo={d.doc_no} offer={d.offer} voided={gone} /> : undefined}
                      rel={{ ctx, kind, id: d.id, docNo: d.doc_no, count: d.linked_count }} />
                    <td style={{ ...td, whiteSpace: 'nowrap', ...strike }}>{dmy(d.date)}</td>
                    <td style={{ ...td, ...strike }}>{d.client_name}</td>
                    {/* Receipts show what was received (net_to_pay) — never `amount`, which is gross of the buyer's WHT. */}
                    <td style={{ ...num, ...strike }} title={isReceipt ? t('aw_col_net') : undefined}>{amt(isReceipt ? d.net_to_pay : d.total_amount)}</td>
                    <td style={{ ...td, ...strike }}>{d.currency}</td>
                    {/* The word the client's portal prints for this row (listCells.tsx salesStatus); d.status stays the stored value. */}
                    <StatusCell<AwDocActions> word={st.word} tone={st.tone} value={offer ? offer.status : d.status} title={proforma ? t('aw_proforma_not_receivable') : undefined}
                      menu={!gone && !readOnly ? { read: () => api.awDocActions(ctx.clientId, kind, d.id), items: (v, why) => (kind === 'quotation' ? stageMenu(d, v, why) : statusMenu(d, v, why)) } : undefined}>
                      {rejected && <SmallLine name="rejected" tone="#B42318"><span data-doc-rejected="">{rejectedLine(t, offer)}</span></SmallLine>}
                      {/* A receipt's Total is net of what the buyer withheld, and a phone has no hover to say so: beside an invoice's Total it would read as a mismatch. */}
                      {isReceipt && !gone && (Number(d.wht_amount) || 0) > 0.005 && <SmallLine name="net">{t('aw_col_net')}</SmallLine>}
                      {showOwing && !noFigure && (Number(d.paid_amount) || 0) > 0.005 && <FigureLine name="paid" label={t('aw_col_paid')} value={d.paid_amount} />}
                      {o > 0.005 && <FigureLine name="outstanding" label={t('aw_col_outstanding')} value={o} owed />}
                      {/* (Round 4.1, decision 2) A credit note moved this figure (it is not Total − Paid): the row
                          says why — "Received X · Credited Y", the server's `credited`, never worked out here. */}
                      {showOwing && !noFigure && (Number(d.credited) || 0) > 0.005 && <SmallLine name="credited"><span data-owed-credited="">{t('p_owed_received_credited', { received: amt(d.paid_amount), credited: amt(d.credited) })}</span></SmallLine>}
                      {gone && d.voided && (
                        <div data-doc-voided="" style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3, minWidth: 170 }}>
                          {voidedLine(t, d.voided)}
                        </div>
                      )}
                    </StatusCell>
                    <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      {!gone && <RowMenu<AwDocActions> busy={busy === d.id || busyEdit === d.id} items={(v, readError) => rowMenu(d, v, readError)}
                        read={readOnly ? undefined : () => api.awDocActions(ctx.clientId, kind, d.id)} />}
                      {/* Stays in the row, outside the menu: a menu that closes as its item is picked would take the input, and
                          the change event, with it. */}
                      {isReceipt && !readOnly && !gone && !docPlainOf(d) && (
                        <input ref={el => { whtInputs.current[d.id] = el; }} type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
                          onChange={e => { const f = e.target.files?.[0]; const el = e.target; attachWht(d, f).finally(() => { el.value = ''; }); }} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* The pager is driven by the page size the SERVER answered with (data.limit), not the one asked
          for: if the two ever differ, a pager counting pages at the client's size would hide the rows
          past the server's. The select still shows what the server used. */}
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      {/* The summary's live counters span a whole table. For quotations and receipts that IS this list;
          the invoices counter also holds tax invoices and notes (every doc_type of the invoices table),
          so it is not printed under Sales › Invoices, whose own total is shown above. */}
      {ctx.summary && (kind === 'quotation' || kind === 'receipt') && (
        <div style={note}>{title} · {t('aw_col_count')}: {kind === 'quotation' ? ctx.summary.counts.quotations : ctx.summary.counts.receipts}</div>
      )}
      {sel && <DocDetail ctx={ctx} kind={kind} doc={sel} onClose={() => setSel(null)} onPdf={() => openPdf(sel)} busyPdf={busy === sel.id} voided={sel.status === 'void'} />}
      {/* ⛔ `fullscreen` ONLY here, never on the portal's own five call sites. The shell offers it
          per caller precisely so the two sides can differ: this is the staff workspace's document
          window (FS-1/FS-3), while a portal customer's screen must not change shape under them.
          The modals already accepted and forwarded the prop — nobody was passing it, so the
          full-screen window existed and no person could reach it. */}
      {/* `layer` too: the host's stacking layer (MerchantDoc/shell.tsx). The staff windows are the
          workspace's, so they sit on WORKSPACE_LAYER — at the portal's default 50 a full-screen window
          would sit UNDER the workspace's toasts. */}
      {stack}
    </div>
  );
}
